import type { Pipeline } from "@/schema"
import { Edge } from "@xyflow/react"

import { EMAIL_PLACEHOLDERS } from "@/components/email/email-placeholders"
import { PlaceholderData } from "@/components/lexical/placeholders"

import { AppNode, AutomationSubjectType, TriggerConfig } from "../types"
import { getStepResultPlaceholders } from "./step-result-placeholders"
import { computeAvailableSubjects, computeUpstreamActionsWithResults } from "./subject-scope"

const GENERAL_PLACEHOLDERS = EMAIL_PLACEHOLDERS.filter((placeholder) => placeholder.category === "General")

const HOUSEHOLD_PLACEHOLDERS: PlaceholderData[] = [
  { id: "household_name", label: "Household name", value: "{{ household.name }}", category: "Household" },
  { id: "household_type", label: "Household type", value: "{{ household.type }}", category: "Household" },
  { id: "household_tier", label: "Tier", value: "{{ household.tier }}", category: "Household" },
  { id: "household_tags", label: "Tags", value: "{{ household.tags }}", category: "Household" },
  {
    id: "household_became_client_at",
    label: "Became client at",
    value: "{{ household.became_client_at }}",
    category: "Household",
  },
  {
    id: "household_next_review_due_on",
    label: "Next review due on",
    value: "{{ household.next_review_due_on }}",
    category: "Household",
  },
  { id: "household_ai_summary", label: "AI summary", value: "{{ household.ai_summary }}", category: "Household" },
  {
    id: "household_leader_preferred_names",
    label: "Leader first name(s)",
    description: 'Uses preferred name if set, falls back to legal first name. Example: "John and Jane".',
    value: "{{ household.leader_preferred_names }}",
    category: "Household",
  },
  {
    id: "household_leader_first_names",
    label: "Leader first name(s) (legal)",
    description: 'Always uses legal first name, never preferred name. Example: "John and Jane".',
    value: "{{ household.leader_first_names }}",
    category: "Household",
  },
  {
    id: "household_next_meeting_at",
    label: "Next meeting start time",
    description: 'Example: "Saturday, February 7 at 8:30am"',
    value: "{{ household.next_meeting_at }}",
    category: "Household",
  },
  {
    id: "household_last_meeting_date",
    label: "Last meeting date",
    description: 'Example: "February 7, 2026"',
    value: "{{ household.last_meeting_date }}",
    category: "Household",
  },
  {
    id: "household_next_meeting_date",
    label: "Next meeting date",
    description: 'Example: "February 7, 2026"',
    value: "{{ household.next_meeting_date }}",
    category: "Household",
  },

  // Head of household attributes
  {
    id: "hoh_first_name",
    label: "First name",
    value: "{{ household.head_of_household.first_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_last_name",
    label: "Last name",
    value: "{{ household.head_of_household.last_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_full_name",
    label: "Full name",
    value: "{{ household.head_of_household.full_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_preferred_name",
    label: "Preferred name",
    value: "{{ household.head_of_household.preferred_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_job_title",
    label: "Job title",
    value: "{{ household.head_of_household.job_title }}",
    category: "Head of household",
  },
  {
    id: "hoh_household_role",
    label: "Household role",
    value: "{{ household.head_of_household.household_role }}",
    category: "Head of household",
  },
  {
    id: "hoh_gender",
    label: "Gender",
    value: "{{ household.head_of_household.gender }}",
    category: "Head of household",
  },
  {
    id: "hoh_marital_status",
    label: "Marital status",
    value: "{{ household.head_of_household.marital_status }}",
    category: "Head of household",
  },
  {
    id: "hoh_date_of_birth",
    label: "Date of birth",
    value: "{{ household.head_of_household.date_of_birth }}",
    category: "Head of household",
  },
  {
    id: "hoh_primary_email_address",
    label: "Primary email address",
    value: "{{ household.head_of_household.primary_email_address }}",
    category: "Head of household",
  },
  {
    id: "hoh_email_addresses",
    label: "All email addresses",
    value: "{{ household.head_of_household.string_email_addresses }}",
    category: "Head of household",
  },
  {
    id: "hoh_primary_phone_number",
    label: "Primary phone number",
    value: "{{ household.head_of_household.primary_phone_number }}",
    category: "Head of household",
  },
  {
    id: "hoh_phone_numbers",
    label: "All phone numbers",
    value: "{{ household.head_of_household.string_phone_numbers }}",
    category: "Head of household",
  },
  {
    id: "hoh_primary_address",
    label: "Primary address",
    value: "{{ household.head_of_household.primary_address }}",
    category: "Head of household",
  },
  {
    id: "hoh_addresses",
    label: "All addresses",
    value: "{{ household.head_of_household.string_addresses }}",
    category: "Head of household",
  },
  {
    id: "hoh_initials",
    label: "Initials",
    value: "{{ household.head_of_household.initials }}",
    category: "Head of household",
  },
  ...EMAIL_PLACEHOLDERS.filter((placeholder) => placeholder.category === "Spouse/partner"),
]

const PERSON_PLACEHOLDERS: PlaceholderData[] = [
  { id: "person_first_name", label: "First name", value: "{{ person.first_name }}", category: "Person" },
  { id: "person_last_name", label: "Last name", value: "{{ person.last_name }}", category: "Person" },
  { id: "person_full_name", label: "Full name", value: "{{ person.full_name }}", category: "Person" },
  { id: "person_preferred_name", label: "Preferred name", value: "{{ person.preferred_name }}", category: "Person" },
  { id: "person_salutation", label: "Salutation", value: "{{ person.salutation }}", category: "Person" },
  { id: "person_designation", label: "Designation", value: "{{ person.designations }}", category: "Person" },
  { id: "person_job_title", label: "Job title", value: "{{ person.job_title }}", category: "Person" },
  { id: "person_gender", label: "Gender", value: "{{ person.gender }}", category: "Person" },
  { id: "person_marital_status", label: "Marital status", value: "{{ person.marital_status }}", category: "Person" },
  { id: "person_date_of_birth", label: "Date of birth", value: "{{ person.date_of_birth }}", category: "Person" },
  {
    id: "person_primary_email_address",
    label: "Primary email address",
    value: "{{ person.primary_email_address }}",
    category: "Person",
  },
  {
    id: "person_email_addresses",
    label: "All email addresses",
    value: "{{ person.string_email_addresses }}",
    category: "Person",
  },
  {
    id: "person_primary_phone_number",
    label: "Primary phone number",
    value: "{{ person.primary_phone_number }}",
    category: "Person",
  },
  {
    id: "person_phone_numbers",
    label: "All phone numbers",
    value: "{{ person.string_phone_numbers }}",
    category: "Person",
  },
  {
    id: "person_primary_address",
    label: "Primary address",
    value: "{{ person.primary_address }}",
    category: "Person",
  },
  { id: "person_addresses", label: "All addresses", value: "{{ person.string_addresses }}", category: "Person" },
  { id: "person_initials", label: "Initials", value: "{{ person.initials }}", category: "Person" },
  { id: "person_household_role", label: "Household role", value: "{{ person.household_role }}", category: "Person" },
]

const TASK_PLACEHOLDERS: PlaceholderData[] = [
  { id: "task_title", label: "Title", value: "{{ task.title }}", category: "Task" },
  { id: "task_description", label: "Description", value: "{{ task.description }}", category: "Task" },
  { id: "task_priority", label: "Priority", value: "{{ task.priority }}", category: "Task" },
  { id: "task_label", label: "Label", value: "{{ task.label }}", category: "Task" },
  { id: "task_deadline_at", label: "Due date", value: "{{ task.deadline_at }}", category: "Task" },
  { id: "task_completed_at", label: "Completed at", value: "{{ task.completed_at }}", category: "Task" },
]

const OPPORTUNITY_PLACEHOLDERS: PlaceholderData[] = [
  { id: "opportunity_name", label: "Name", value: "{{ opportunity.name }}", category: "Opportunity" },
  { id: "opportunity_value", label: "Value", value: "{{ opportunity.value }}", category: "Opportunity" },
  {
    id: "opportunity_probability",
    label: "Probability",
    value: "{{ opportunity.probability }}",
    category: "Opportunity",
  },
  { id: "opportunity_stage", label: "Stage", value: "{{ opportunity.stage_name }}", category: "Opportunity" },
  {
    id: "opportunity_close_date",
    label: "Close date",
    value: "{{ opportunity.target_close_date }}",
    category: "Opportunity",
  },
]

export const PROJECT_PLACEHOLDERS: PlaceholderData[] = [
  { id: "project_name", label: "Name", value: "{{ project.name }}", category: "Project" },
  { id: "project_status", label: "Status", value: "{{ project.status }}", category: "Project" },
  { id: "project_progress", label: "Progress", value: "{{ project.overall_progress }}", category: "Project" },
]

const MEETING_PLACEHOLDERS: PlaceholderData[] = [
  { id: "meeting_title", label: "Title", value: "{{ meeting.title }}", category: "Meeting" },
  {
    id: "meeting_start_time",
    label: "Start time",
    description: 'Example: "Saturday, February 7 at 8:30am"',
    value: "{{ meeting.start_time }}",
    category: "Meeting",
  },
  {
    id: "meeting_end_time",
    label: "End time",
    description: 'Example: "Saturday, February 7 at 9am"',
    value: "{{ meeting.end_time }}",
    category: "Meeting",
  },
  { id: "meeting_location", label: "Location", value: "{{ meeting.location }}", category: "Meeting" },
  { id: "meeting_description", label: "Description", value: "{{ meeting.description }}", category: "Meeting" },
  {
    id: "meeting_ai_summary",
    label: "AI summary",
    description: "The meeting's AI summary, formatted",
    value: "{{ meeting.ai_summary }}",
    category: "Meeting",
    richTextOnly: true,
  },
  {
    id: "meeting_ai_summary_markdown",
    label: "AI summary",
    description: "The meeting's AI summary, formatted as Markdown",
    value: "{{ meeting.ai_summary_markdown }}",
    category: "Meeting",
  },
]

const EMAIL_MESSAGE_PLACEHOLDERS: PlaceholderData[] = [
  { id: "email_subject", label: "Subject", value: "{{ email_message.subject }}", category: "Email" },
  { id: "email_from", label: "From", value: "{{ email_message.from }}", category: "Email" },
  { id: "email_to", label: "To", value: "{{ email_message.to }}", category: "Email" },
  { id: "email_body", label: "Body", value: "{{ email_message.body }}", category: "Email" },
  { id: "email_received_at", label: "Received at", value: "{{ email_message.received_at }}", category: "Email" },
]

const SENDER_PLACEHOLDERS: PlaceholderData[] = [
  { id: "user_first_name", label: "Sender first name", value: "{{ user.first_name }}", category: "Sender" },
  { id: "user_last_name", label: "Sender last name", value: "{{ user.last_name }}", category: "Sender" },
  { id: "user_full_name", label: "Sender full name", value: "{{ user.full_name }}", category: "Sender" },
  {
    id: "user_scheduling_link",
    label: "Sender scheduling link",
    value: "{{ user.scheduling_link | default: book.scheduling_link }}",
    category: "Sender",
  },
]

const COMPANY_PLACEHOLDERS: PlaceholderData[] = [
  { id: "book_scheduling_link", label: "Scheduling link", value: "{{ book.scheduling_link }}", category: "Company" },
  { id: "company_name", label: "Company name", value: "{{ company.name }}", category: "Company" },
]

const CONTACT_PLACEHOLDERS: PlaceholderData[] = [
  { id: "contact_full_name", label: "Full name", value: "{{ contact.full_name }}", category: "Contact" },
  {
    id: "contact_primary_email_address",
    label: "Primary email address",
    value: "{{ contact.primary_email_address }}",
    category: "Contact",
  },
  {
    id: "contact_primary_phone_number",
    label: "Primary phone number",
    value: "{{ contact.primary_phone_number }}",
    category: "Contact",
  },
]

const BUSINESS_PLACEHOLDERS: PlaceholderData[] = [
  { id: "business_name", label: "Business name", value: "{{ business.name }}", category: "Business" },
  { id: "business_industry", label: "Industry", value: "{{ business.industry }}", category: "Business" },
  { id: "business_website", label: "Website", value: "{{ business.website_url }}", category: "Business" },
]

export const ALL_AUTOMATION_PLACEHOLDERS: PlaceholderData[] = [
  ...GENERAL_PLACEHOLDERS,
  ...HOUSEHOLD_PLACEHOLDERS,
  ...PERSON_PLACEHOLDERS,
  ...TASK_PLACEHOLDERS,
  ...OPPORTUNITY_PLACEHOLDERS,
  ...PROJECT_PLACEHOLDERS,
  ...MEETING_PLACEHOLDERS,
  ...EMAIL_MESSAGE_PLACEHOLDERS,
  ...CONTACT_PLACEHOLDERS,
  ...BUSINESS_PLACEHOLDERS,
  ...SENDER_PLACEHOLDERS,
  ...COMPANY_PLACEHOLDERS,
]

const SUBJECT_PLACEHOLDER_MAP: Record<AutomationSubjectType, PlaceholderData[]> = {
  household: HOUSEHOLD_PLACEHOLDERS,
  person: PERSON_PLACEHOLDERS,
  task: TASK_PLACEHOLDERS,
  opportunity: OPPORTUNITY_PLACEHOLDERS,
  project: PROJECT_PLACEHOLDERS,
  meeting: MEETING_PLACEHOLDERS,
  email_message: EMAIL_MESSAGE_PLACEHOLDERS,
  contact: CONTACT_PLACEHOLDERS,
  business: BUSINESS_PLACEHOLDERS,
}

const OUTBOUND_BLOCKED_PLACEHOLDER_IDS = [
  "household_ai_summary",
  "meeting_ai_summary",
  "meeting_ai_summary_markdown",
  "meeting_ai_summary_plaintext",
  "meeting_quick_summary",
  "meeting_timestamped_transcript",
  "meeting_plaintext_transcript",
  "meeting_transcript_for_context",
]

interface PlaceholderOptions {
  includeSenderAndCompany?: boolean
  includeStepResults?: boolean
  includeSensitiveContent?: boolean
}

/**
 * Gets placeholders for a set of subject types.
 * Optionally includes sender and company placeholders (default: true).
 */
export function getPlaceholdersForSubjects(
  subjects: Set<AutomationSubjectType>,
  options: PlaceholderOptions = {}
): PlaceholderData[] {
  const { includeSenderAndCompany = true, includeSensitiveContent = true } = options
  const placeholders: PlaceholderData[] = [...GENERAL_PLACEHOLDERS]

  const subjectArray = Array.from(subjects)
  for (const subject of subjectArray) {
    const subjectPlaceholders = SUBJECT_PLACEHOLDER_MAP[subject]
    if (subjectPlaceholders) {
      placeholders.push(
        ...subjectPlaceholders.filter(
          (placeholder) => includeSensitiveContent || !OUTBOUND_BLOCKED_PLACEHOLDER_IDS.includes(placeholder.id)
        )
      )
    }
  }

  if (includeSenderAndCompany) {
    placeholders.push(...SENDER_PLACEHOLDERS)
    placeholders.push(...COMPANY_PLACEHOLDERS)
  }

  return placeholders
}

/**
 * Gets placeholders available at a specific node in the automation.
 * Computes the available subjects at the node and returns their placeholders.
 * Optionally includes step result placeholders from upstream actions.
 */
export function getPlaceholdersForNode(
  nodes: AppNode[],
  edges: Edge[],
  nodeId: string,
  options: PlaceholderOptions = {},
  trigger?: TriggerConfig,
  pipelines?: Pipeline[]
): PlaceholderData[] {
  const { includeStepResults = true, includeSensitiveContent = true } = options
  const subjects = computeAvailableSubjects(nodes, edges, nodeId, trigger, pipelines)
  const placeholders = getPlaceholdersForSubjects(subjects, options)

  if (includeStepResults) {
    const upstreamActions = computeUpstreamActionsWithResults(nodes, edges, nodeId)
    placeholders.push(...getStepResultPlaceholders(upstreamActions, includeSensitiveContent))
  }

  return placeholders
}
